import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileText, AlertTriangle } from 'lucide-react';
import { MAX_DOCUMENT_SIZE_BYTES, MAX_DOCUMENT_SIZE_MB } from '../constants/upload';
import { COLORS } from '../theme/colors';
import FileDropzone from './ui/FileDropzone';

const MAX_FILES = 50;

function looksLikePdf(file) {
  if (file.size === 0) return false;
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.pdf')) return true;
  if (file.type === 'application/pdf') return true;
  return false;
}

function ReceiptUpload({ files, onFilesChange, disabled }) {
  const { t } = useTranslation('receipts');

  const addFiles = useCallback((incoming) => {
    const allowed = MAX_FILES - files.length;
    const next = [...files];
    for (let i = 0; i < Math.min(incoming.length, allowed); i++) {
      const f = incoming[i];
      if (f.size > MAX_DOCUMENT_SIZE_BYTES) continue;
      next.push(f);
    }
    onFilesChange(next);
  }, [files, onFilesChange]);

  const removeAt = (idx) => {
    onFilesChange(files.filter((_, i) => i !== idx));
  };

  const clearAll = () => onFilesChange([]);

  return (
    <>
      <FileDropzone
        accept=".pdf,application/pdf"
        multiple
        disabled={disabled}
        title={t('process.dropTitle')}
        hint={t('process.dropHint', { max: MAX_FILES, maxMb: MAX_DOCUMENT_SIZE_MB })}
        onFiles={addFiles}
      />

      {files.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {t('process.selectedFiles', { count: files.length })}
            </span>
            <button type="button" className="btn btn-ghost" onClick={clearAll} disabled={disabled}>
              {t('process.clearAll')}
            </button>
          </div>
          <ul className="receipts-file-list">
            {files.map((f, i) => {
              const isInvalid = !looksLikePdf(f);
              return (
                <li key={`${f.name}-${i}`} className={`receipts-file-item ${isInvalid ? 'invalid' : ''}`}>
                  {isInvalid ? <AlertTriangle size={16} color={COLORS.warningHover} /> : <FileText size={16} />}
                  <span className="receipts-file-name" title={f.name}>{f.name}</span>
                  <span className="receipts-file-size">
                    {(f.size / 1024).toFixed(0)} KB
                  </span>
                  <button
                    type="button"
                    className="receipts-file-remove"
                    onClick={() => removeAt(i)}
                    aria-label={t('process.removeFile')}
                    disabled={disabled}
                  >
                    <X size={16} />
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
}

export { looksLikePdf, MAX_FILES };
export default ReceiptUpload;
